/**
 * The device's top safe area (iPad status bar) in the desktop/tablet layout.
 * The topmost element of the page column varies per page and state (top bar,
 * navigation bar, custom `PageHeader`, `PageSubHeader`, or the content), so
 * `Page` publishes `--page-top-inset` on whichever element is its first child.
 * Each element that can be first adds the variable to its own top padding via
 * `belowPageTopInset`, so its own padding is kept and a sticky bar's background
 * still covers the status bar while scrolling. Anywhere else, and in desktop
 * browsers, the variable is 0.
 */
import type { CSSObject } from "@mui/material/styles";

export const PAGE_TOP_INSET_VAR = "--page-top-inset";

/** A CSS length raised by the page's top inset (0 unless the element is first). */
export function belowPageTopInset(base: string): string {
  return `calc(${base} + var(${PAGE_TOP_INSET_VAR}, 0px))`;
}

/**
 * The page column's rule: publish the top safe area on its first child only.
 * It sets nothing but the variable, so it overrides none of the child's styles.
 */
export const publishPageTopInsetOnFirstChild: CSSObject = {
  "& > :first-child": {
    [PAGE_TOP_INSET_VAR]: "env(safe-area-inset-top)",
  },
};

/**
 * Registers the variable as non-inheriting, so only the first child itself
 * gets the inset, never an element nested inside it.
 */
export const pageTopInsetGlobalStyles: CSSObject = {
  [`@property ${PAGE_TOP_INSET_VAR}`]: {
    syntax: "'<length>'",
    inherits: "false",
    initialValue: "0px",
  },
};
